// Generic render-side map driver.
//
// Given a MapArena (sim) + MapBundle (json + loaders), builds the visual
// scene: sky sphere, ground/platform primitives, lights, then calls each
// behavior's init(render-layer ctx) so behaviors can spawn meshes parented
// under `this.group`. Public surface mirrors the legacy ArenaView class so
// Renderer.ts doesn't care which one it's holding.

import * as THREE from 'three';
import { createGLTFLoader } from './glbLoader.js';
import type { MapArena } from '../sim/MapArena';
import type { MapBundle } from '../maps/registry';
import type { Behavior, MapContext } from '../maps/Behavior';
import type {
  AmbientLightDef, ColorHex, DirectionalLightDef,
  LightDef, MapJSON, ModelRefObject, ObjectNode,
  PointLightDef, Vec3
} from '../maps/MapJSON';
import {
  buildInlinePBR, buildPresetMaterial, SKY_PRESET_IDS
} from './materials/presets';
import {
  isMaterialModule, isMaterialPreset,
  type AudioDef, type IblDef, type MaterialRef
} from '../maps/MapJSON';
import { resolveUrl as resolveAssetUrl } from '../ui/editor/EditorAssets';

interface LiveMaterial {
  material: THREE.Material;
  setTime?: (t: number) => void;
  setDissolve?: (v: number) => void;
}

export class MapArenaView {
  readonly group = new THREE.Group();
  readonly mapId: string;

  /** Sky sphere mesh. Public so previews can reparent it (detaching from the
   *  spinning map group) without reaching into children. Matches legacy
   *  ArenaView.skyMesh. */
  readonly skyMesh: THREE.Mesh | null = null;
  /** Minimal contract mirroring legacy ArenaView.skyMaterial — the Renderer
   *  may poke setSaturnVisibility / setOrangeTint through this if the
   *  material supports them. */
  readonly skyMaterial: { setTime(t: number): void } | null = null;

  /** World-space direction toward the key light for this map. Renderer
   *  positions its directional light along this so toon shading agrees with
   *  the sky / ring / rock shaders. */
  readonly sunDir?: THREE.Vector3;

  /** Legacy ArenaView-compat: behaviors that parent a ship under a group and
   *  register it as 'ship' expose it here so external callers (FxBus uses
   *  this to attach the thruster audio loop) can mount things under it
   *  without knowing that the mount comes from a behavior. */
  get shipGroup(): THREE.Object3D | undefined {
    return this.objectById.get('ship');
  }

  /** Audio config from map.json — FxBus reads this to wire reverb,
   *  footsteps, music, and per-SFX wet sends without string-matching on
   *  `mapId`. Undefined on maps that don't declare any audio. */
  get audio(): AudioDef | undefined {
    return this.json.audio;
  }

  /** Water surface Y from `properties.waterY`. Read by render-side systems
   *  (camera-submersion → underwater post pass + lowpass) without going
   *  through a behavior. Undefined on dry maps. */
  get waterY(): number | undefined {
    return this.json.properties?.waterY;
  }

  /** Wave-aware water surface Y at world (x, z). Composes flat `waterY`
   *  with any behavior that exposes `sampleWaveOffset(x, z)` (currently
   *  the sinking-ship ocean) so consumers comparing a world point to the
   *  surface — e.g. the audio lowpass deciding whether the camera is
   *  under water — track the live wave instead of a flat plane. Returns
   *  undefined on dry maps so call sites can gate on it the same way
   *  they already gate on `waterY`. */
  waterSurfaceYAt(x: number, z: number): number | undefined {
    const baseY = this.json.properties?.waterY;
    if (baseY === undefined) return undefined;
    for (const b of this.arena.behaviorList) {
      const candidate = b as unknown as { sampleWaveOffset?: (x: number, z: number) => number };
      if (typeof candidate.sampleWaveOffset === 'function') {
        return baseY + candidate.sampleWaveOffset(x, z);
      }
    }
    return baseY;
  }

  /** IBL config from map.json. Renderer applies this onto CONFIG.render at
   *  init() so per-map authoring drives the starting look without code. */
  get ibl(): IblDef | undefined {
    return this.json.ibl;
  }

  /** Resolve a behavior instance by id. Exposed so non-behavior consumers
   *  (e.g. FxBus, which reads `sinkProgress` off the sinking-ship behavior
   *  to drive a crescendo loop) can read state without a full registry
   *  refactor. Returns undefined when the active map doesn't declare the
   *  requested id. */
  getBehavior<T extends Behavior = Behavior>(id: string): T | undefined {
    return this.arena.behaviorDefsById.get(id) as T | undefined;
  }

  /** Material instances with per-frame setTime hooks. Pumped from setTime. */
  private liveMaterials: LiveMaterial[] = [];
  /** The THREE objects created from primitives, keyed by node id (if any).
   *  Exposed readonly so the editor can iterate for selection + gizmos. */
  readonly objectById = new Map<string, THREE.Object3D>();

  private arena: MapArena;
  private json: MapJSON;
  private bundle: MapBundle;
  private _camera: THREE.Camera | null = null;
  private _audioListener: THREE.AudioListener | null = null;

  constructor(arena: MapArena, bundle: MapBundle) {
    this.arena = arena;
    this.bundle = bundle;
    this.json = bundle.json;
    this.mapId = bundle.json.id;

    // 0) Top-level sunDir property — exposed to the Renderer so it can
    //    position its default directional light along the same axis the sky
    //    and rock shaders are sampling. A directional light declared under
    //    `lights` with id 'sun' still takes precedence over this via the
    //    lights-loop below.
    const sunDirProp = this.json.properties?.sunDir;
    if (sunDirProp) {
      (this as { sunDir?: THREE.Vector3 }).sunDir =
        new THREE.Vector3(...sunDirProp).normalize();
    }

    // 1) Sky: always an inverted sphere if the map declared one.
    if (this.json.sky) {
      const { material, setTime } = buildPresetMaterial(
        this.json.sky.preset,
        this.json.sky.params ?? {}
      );
      if (!SKY_PRESET_IDS.has(this.json.sky.preset)) {
        // Non-sky preset referenced in the sky slot — likely authoring error.
        console.warn(`[MapArenaView] ${this.mapId}: sky preset "${this.json.sky.preset}" isn't registered as a sky preset; rendering anyway.`);
      }
      const skyMesh = new THREE.Mesh(new THREE.SphereGeometry(400, 64, 32), material);
      skyMesh.renderOrder = -1;
      this.group.add(skyMesh);
      this.skyMesh = skyMesh;
      if (setTime) {
        this.skyMaterial = { setTime };
        this.liveMaterials.push({ material, setTime });
      }
    }

    // 2) Lights
    for (const light of this.json.lights ?? []) {
      const obj = buildLight(light);
      if (!obj) continue;
      this.group.add(obj);
      if (light.id) this.objectById.set(light.id, obj);
      if (light.type === 'directional') {
        // Expose `sunDir` for the Renderer if the author tagged this light
        // as the sun. A directional light with id 'sun' — or the first one
        // declared — wins.
        const dirDef = light as DirectionalLightDef;
        if (dirDef.direction && (!this.sunDir || light.id === 'sun')) {
          (this as { sunDir?: THREE.Vector3 }).sunDir =
            new THREE.Vector3(...dirDef.direction).normalize();
        }
      }
    }

    // 3) Primitives
    for (const node of this.json.objects ?? []) {
      const obj = this.buildObject(node);
      if (obj) this.group.add(obj);
    }
  }

  /** Await async resources (model loads, behavior render-init). Call after
   *  constructor. Kept separate from constructor so `new MapArenaView(...)`
   *  remains synchronous for symmetry with legacy ArenaView. */
  async initRenderBehaviors(): Promise<void> {
    const ctx = this.makeContext();
    // Share the same behavior instances the sim created. Each one sees its
    // own second init() call with the render-layer ctx.
    for (const [id, behavior] of this.arena.behaviorDefsById) {
      const def = this.json.behaviors?.find((d) => d.id === id);
      behavior.init?.(ctx, def?.params ?? {});
    }
    // After behaviors have had a chance to populate their modelRef
    // placeholders, fall back to a generic GLTF loader for any modelRef the
    // map declared without a backing behavior. This is what makes the editor
    // "drop a .glb and it just appears" path work, and it lets future maps
    // ship static models without a one-line behavior.
    await this.loadGenericModelRefs();
  }

  /** Optional callback fired when one or more modelRef placeholders finish
   *  populating. The editor uses this to refresh selection-highlight shells
   *  after async geometry arrives. */
  onObjectsPopulated: ((ids: string[]) => void) | null = null;

  private async loadGenericModelRefs(): Promise<void> {
    const todo: { id: string; group: THREE.Group; node: ModelRefObject }[] = [];
    for (const [id, obj] of this.objectById) {
      const node = (obj as unknown as { __modelRef?: ModelRefObject }).__modelRef;
      if (!node) continue;
      // Skip placeholders already filled by a behavior.
      if (obj.children.length > 0) continue;
      todo.push({ id, group: obj as THREE.Group, node });
    }
    if (todo.length === 0) return;
    const loader = createGLTFLoader();
    await Promise.all(todo.map(({ id, group, node }) => new Promise<void>((resolve) => {
      const url = resolveAssetUrl(node.src);
      loader.load(url, (gltf) => {
        const root = gltf.scene;
        if (node.scaleToMaxDim && node.scaleToMaxDim > 0) {
          const box = new THREE.Box3().setFromObject(root);
          const size = new THREE.Vector3(); box.getSize(size);
          const max = Math.max(size.x, size.y, size.z);
          if (max > 0) root.scale.setScalar(node.scaleToMaxDim / max);
        }
        if (node.yawDeg) root.rotation.y = node.yawDeg * Math.PI / 180;
        root.traverse((c) => {
          if ((c as THREE.Mesh).isMesh) {
            (c as THREE.Mesh).castShadow = true;
            (c as THREE.Mesh).receiveShadow = true;
          }
        });
        group.add(root);
        this.onObjectsPopulated?.([id]);
        resolve();
      }, undefined, (err) => {
        console.warn(`[MapArenaView] generic modelRef "${id}" load failed: ${node.src}`, err);
        resolve();
      });
    })));
  }

  private makeContext(): MapContext {
    const self = this;
    return {
      mapId: this.mapId,
      layer: 'render',
      // Render-layer behaviors shouldn't be creating physics bodies, but we
      // expose the world so they can read it for sim→render sync without
      // an extra indirection.
      rapier: this.arena.rapierWorld,
      scene: null,
      mapGroup: this.group,
      time: () => self.arena.timeSec,
      authoritative: self.arena.authoritative,
      getObject3D: (id) => self.objectById.get(id),
      getBody: (id) => self.arena.bodyById.get(id),
      getCollider: (id) => self.arena.colliderById.get(id),
      getBehavior: <T extends Behavior>(id: string) =>
        self.arena.behaviorDefsById.get(id) as T | undefined,
      registerObject3D: (id, obj) => {
        self.objectById.set(id, obj);
        if (obj.parent == null) self.group.add(obj);
      },
      registerBody: () => { /* render behaviors shouldn't create bodies */ },
      registerCollider: () => { /* render behaviors shouldn't create colliders */ },
      characters: () => self.arena.characterList,
      // Render-layer behaviors don't observe combat events — only the sim
      // layer's MapContext exposes them. Always empty here.
      recentImpacts: () => [],
      camera: () => self._camera,
      audioListener: () => self._audioListener
    };
  }

  /** Renderer hands the active camera to the view each frame so render
   *  behaviors can do billboarding, frustum culls, etc. */
  setActiveCamera(cam: THREE.Camera | null): void { this._camera = cam; }

  /** Renderer hands its AudioListener to the view once after CameraRig
   *  is constructed. Behaviors that want their own positional audio
   *  (loops, one-shots) read this at init time. */
  setAudioListener(l: THREE.AudioListener | null): void { this._audioListener = l; }

  private buildObject(node: ObjectNode): THREE.Object3D | null {
    if (node.type === 'group') {
      const g = new THREE.Group();
      applyTransform(g, node.transform);
      for (const child of node.children ?? []) {
        const co = this.buildObject(child);
        if (co) g.add(co);
      }
      if (node.id) this.objectById.set(node.id, g);
      return g;
    }
    if (node.type === 'modelRef') {
      // Model refs are async — a behavior (typically spaceshipTilt or a
      // thin "staticModel" behavior) handles loading. Leave a group
      // placeholder so the id resolves.
      const placeholder = new THREE.Group();
      applyTransform(placeholder, node.transform);
      attachModelRefLoadHint(placeholder, node);
      if (node.id) this.objectById.set(node.id, placeholder);
      return placeholder;
    }

    const geo = buildGeometry(node);
    if (!geo) return null;
    // AO/light maps require a second UV channel (`uv1` in three r152+).
    // Clone the primary UV unconditionally so any inline-PBR material with
    // an `mapAo` slot just works without per-shape special-casing.
    if (geo.attributes.uv && !geo.attributes.uv1) {
      geo.setAttribute('uv1', geo.attributes.uv);
    }
    const mat = this.resolveMaterial(node.material);
    const mesh = new THREE.Mesh(geo, mat.material);
    // Map primitives participate in the shadow pass. The sky sphere is
    // built separately (constructor) and never reaches this branch, so it
    // stays unflagged — its inverted backside would otherwise write
    // garbage depth and bleed across the level.
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    applyTransform(mesh, node.transform);
    if (mat.setTime || mat.setDissolve) {
      this.liveMaterials.push({
        material: mat.material,
        setTime: mat.setTime,
        setDissolve: mat.setDissolve
      });
    }
    if (node.id) this.objectById.set(node.id, mesh);
    return mesh;
  }

  private resolveMaterial(ref: MaterialRef | undefined): LiveMaterial {
    if (!ref) {
      return { material: new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 1 }) };
    }
    if (isMaterialPreset(ref)) {
      const { material, setTime } = buildPresetMaterial(ref.preset, ref.params ?? {});
      const setDissolve = (material as { setDissolve?: (v: number) => void }).setDissolve
        ?.bind(material);
      return { material, setTime, setDissolve };
    }
    if (isMaterialModule(ref)) {
      // Custom bundle-scoped material. The loader runs async — we fall back
      // to a neutral placeholder until the real material resolves. A future
      // phase (editor) adds a proper await path; for v1 the preset system
      // covers every existing map.
      const placeholder = new THREE.MeshStandardMaterial({ color: 0xff00ff });
      const loader = this.bundle.materialLoaders[ref.module];
      if (loader) {
        void loader().then((mod) => {
          const ctor = mod.default as new (params: Record<string, unknown>) => THREE.Material;
          const real = new ctor(ref.params ?? {});
          placeholder.dispose();
          // Swap in the real material on every mesh using the placeholder.
          // Traverse the group; cheap for now (called once per custom ref).
          this.group.traverse((obj) => {
            const m = (obj as THREE.Mesh).material;
            if (m === placeholder) (obj as THREE.Mesh).material = real;
          });
          const setTimeFn = (real as { setTime?: (t: number) => void }).setTime?.bind(real);
          if (setTimeFn) this.liveMaterials.push({ material: real, setTime: setTimeFn });
        }).catch((err) => {
          console.error(`[MapArenaView] ${this.mapId}: failed to load material module "${ref.module}"`, err);
        });
      } else {
        console.warn(`[MapArenaView] ${this.mapId}: material module "${ref.module}" not found in bundle`);
      }
      return { material: placeholder };
    }
    return { material: buildInlinePBR(ref) };
  }

  setDissolve(v: number): void {
    for (const lm of this.liveMaterials) lm.setDissolve?.(v);
  }

  setTime(t: number): void {
    for (const lm of this.liveMaterials) lm.setTime?.(t);
    for (const b of this.arena.behaviorList) b.stepRender?.(t, 0, 0);
  }

  /** Called by the Renderer each frame with full timing info. Replaces the
   *  ad-hoc setTime + emitter-update pumps in the legacy per-map branches. */
  stepRender(t: number, dt: number, alpha: number): void {
    for (const lm of this.liveMaterials) lm.setTime?.(t);
    for (const b of this.arena.behaviorList) b.stepRender?.(t, dt, alpha);
  }

  /** Match-teardown: stop and unparent any render-layer resources behaviors
   *  spawned (positional audio, ad-hoc meshes). Mirrors `MapArena.disposeBehaviors`
   *  for the sim layer; both share the same behavior instances. */
  disposeBehaviors(): void {
    for (const b of this.arena.behaviorList) b.dispose?.('render');
  }
}

// ---------------------------------------------------------------------------
// Helpers

function applyTransform(obj: THREE.Object3D, t: { p?: Vec3; r?: Vec3; s?: Vec3 | number } | undefined): void {
  if (!t) return;
  if (t.p) obj.position.set(t.p[0], t.p[1], t.p[2]);
  if (t.r) obj.rotation.set(t.r[0], t.r[1], t.r[2]);
  if (t.s !== undefined) {
    if (typeof t.s === 'number') obj.scale.setScalar(t.s);
    else obj.scale.set(t.s[0], t.s[1], t.s[2]);
  }
}

function buildGeometry(node: ObjectNode): THREE.BufferGeometry | null {
  switch (node.type) {
    case 'box': {
      const [x, y, z] = node.size;
      return new THREE.BoxGeometry(x, y, z);
    }
    case 'sphere': {
      const [w, h] = node.segments ?? [32, 16];
      return new THREE.SphereGeometry(node.radius, w, h);
    }
    case 'cylinder': {
      return new THREE.CylinderGeometry(
        node.radiusTop, node.radiusBottom, node.height,
        node.radialSegments ?? 32
      );
    }
    case 'capsule': {
      const g = new THREE.CapsuleGeometry(node.radius, node.halfHeight * 2, 4, 16);
      if (node.axis === 'x') g.rotateZ(Math.PI / 2);
      else if (node.axis === 'z') g.rotateX(Math.PI / 2);
      return g;
    }
    case 'plane': {
      const [w, h] = node.size;
      const [ws, hs] = node.segments ?? [1, 1];
      return new THREE.PlaneGeometry(w, h, ws, hs);
    }
    case 'circle': {
      return new THREE.CircleGeometry(node.radius, node.segments ?? 32);
    }
    default: return null;
  }
}

function buildLight(def: LightDef): THREE.Object3D | null {
  const parseColor = (c: ColorHex | undefined, fallback: number): THREE.Color =>
    new THREE.Color(c ?? fallback);
  switch (def.type) {
    case 'ambient': {
      const a = def as AmbientLightDef;
      return new THREE.AmbientLight(parseColor(a.color, 0xffffff), a.intensity ?? 1);
    }
    case 'directional': {
      const d = def as DirectionalLightDef;
      const l = new THREE.DirectionalLight(parseColor(d.color, 0xffffff), d.intensity ?? 1);
      if (d.direction) l.position.set(...d.direction).multiplyScalar(10);
      return l;
    }
    case 'point': {
      const p = def as PointLightDef;
      const l = new THREE.PointLight(
        parseColor(p.color, 0xffffff), p.intensity ?? 1,
        p.range ?? 0, p.decay ?? 2
      );
      l.position.set(...p.position);
      return l;
    }
    default: return null;
  }
}

/** Tag a modelRef placeholder group with its src / scale data so a loading
 *  behavior can find it via ctx.getObject3D(id) and populate it. */
function attachModelRefLoadHint(group: THREE.Group, node: ModelRefObject): void {
  (group as unknown as { __modelRef?: ModelRefObject }).__modelRef = node;
}
